import { useEffect, useMemo, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import { Box, Button, Chip, Grid, IconButton, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import { get } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import LeaveMetricCard from 'components/SS/LeaveMetricCard';
import ApplyLeaveModal from 'components/SS/applyLeaveModel';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';

const MyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await post('/admin/getLeaves', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: '',
      });
      if (res.status == 200) {
        setLeaves(res.data.data);
        setTotal(res.data.total);
      } else {
        enqueueSnackbar(res.message || 'Error While Fetching Data', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error While Fetching Data', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveBalance = async () => {
    const res = await get('/admin/leaveBalance');
    if (res.status == 200) {
      setLeaveBalance(res.data);
    } else {
      enqueueSnackbar(res.message || 'Error While Fetching Data', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  useEffect(() => {
    fetchLeaveBalance();
  }, []);
  useEffect(() => {
    fetchLeaves();
  }, [paginationModel]);

  const EditButton = ({ row }) => {
    return (
      <IconButton
        onClick={() => {
          setOpen(true);
          setEdit(row);
        }}
      >
        <IconifyIcon icon="material-symbols:edit-outline-rounded" sx={{ fontSize: 20 }} />
      </IconButton>
    );
  };

  const columns = useMemo(
    () => [
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        minWidth: 90,
        renderCell: (params) => {
          return (
            <>
              {/* <ViewButton row={params.row} /> */}

              {params.row.status === 'PENDING' && <EditButton row={params.row} />}
            </>
          );
        },
      },
      {
        field: 'leaveType',
        headerName: 'Leave Type',
        minWidth: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
            {params.row.leaveType}
          </Typography>
        ),
      },
      {
        field: 'fromDate',
        headerName: 'From Date',
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.fromDate).format('DD/MM/YYYY')}
          </Typography>
        ),
      },
      {
        field: 'toDate',
        headerName: 'To Date',
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.toDate).format('DD/MM/YYYY')}
          </Typography>
        ),
      },
      {
        field: 'totalDays',
        headerName: 'Days',
        minWidth: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => <Typography variant="subtitle2">{params.row.quantity}</Typography>,
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const getColor = (status) => {
            switch (status) {
              case 'APPROVED':
                return 'success';
              case 'REJECTED':
                return 'error';
              case 'PENDING':
                return 'warning';
              default:
                return 'default';
            }
          };

          return (
            <Chip
              label={params.row.status}
              color={getColor(params.row.status)}
              size="small"
              sx={{ fontWeight: 500 }}
            />
          );
        },
      },
      {
        field: 'appliedOn',
        headerName: 'Update On',
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.updatedAt).format('DD/MM/YYYY HH:mm')}
          </Typography>
        ),
      },
      {
        field: 'approvedBy',
        headerName: 'Approved By',
        minWidth: 180,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => {
          const approvedBy = params.row?.approvedBy;
          return (
            <Typography variant="subtitle2">
              {approvedBy ? `${approvedBy.firstName} ${approvedBy.lastName}` : '-'}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  return (
    <Box p={4}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" fontWeight={600} mb={2}>
          My Leaves
        </Typography>
        <Button
          variant="soft"
          shape="square"
          color="neutral"
          sx={{ gap: 0.5, p: 1 }}
          onClick={() => setOpen(true)}
        >
          <IconifyIcon icon="material-symbols:add-2-rounded" />
          Apply Leave
        </Button>
      </Box>
      {/* 🔹 Top Cards */}
      <Grid container spacing={3} mb={2}>
        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <LeaveMetricCard
            title="Paid Leave"
            value={leaveBalance ? leaveBalance.plAvailable : 0}
            subtitle="Available"
            icon={<EventIcon />}
            bgColor="#E6F7F5"
            // bgColor="#E3F2FD"
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <LeaveMetricCard
            title="Sick Leave"
            value={leaveBalance ? leaveBalance.slAvailable : 0}
            subtitle="Available"
            icon={<EventIcon />}
            bgColor="#E8F1FD"
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <LeaveMetricCard
            title="Approved PL"
            value={leaveBalance ? leaveBalance.plUsed : 0}
            subtitle="Total"
            icon={<CheckCircleIcon />}
            bgColor="#E9F9EE"
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <LeaveMetricCard
            title="Approved SL"
            value={leaveBalance ? leaveBalance.slUsed : 0}
            subtitle="Total"
            icon={<CheckCircleIcon />}
            bgColor="#ECFDF5"
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <LeaveMetricCard
            title="Rejected"
            value={leaveBalance ? leaveBalance.rejectedLeave : 0}
            subtitle="Total"
            icon={<CancelIcon />}
            bgColor="#FEECEC"
          />
        </Grid>
      </Grid>
      <Box sx={{ width: '100%' }}>
        <DataGrid
          loading={loading}
          rows={leaves}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel} // { page: 0, pageSize: 10 }
          onPaginationModelChange={(e) => {
            setPaginationModel(() => ({
              page: e.page,
              pageSize: e.pageSize,
              search: '',
            }));
          }}
          pageSizeOptions={[5, 10, 20]}
          slots={{
            basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
          }}
        />
      </Box>
      <ApplyLeaveModal
        open={open}
        edit={edit}
        handleClose={() => {
          setEdit(null);
          setOpen(false);
          fetchLeaveBalance();
          fetchLeaves();
        }}
      />
    </Box>
  );
};

export default MyLeaves;
